import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InfoIcon,
  ErrorIcon,
  WarningIcon,
  CheckCircleIcon,
} from '@common/components/icons';
import * as styles from './StatusPopUpModal.module.css';

const icons = {
  info: InfoIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  success: CheckCircleIcon,
};

const buttonClasses = {
  info: 'buttonInfo',
  error: 'buttonError',
  warning: 'buttonWarning',
  success: 'buttonSuccess',
};

export default function StatusPopUpModal({
  type = 'info',
  title,
  message,
  buttonText,
  onClose,
  isOpen = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEscape]);

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not its children
    if (e.target === e.currentTarget && onClose && closeOnOverlayClick) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const displayTitle = title || t(`common.statusPopUp.${type}`);
  const displayButtonText = buttonText || t('common.okay');
  const Icon = icons[type] || icons.info;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className={`${styles.modal} ${styles[type] || ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="status-popup-title"
      >
        {/* Close Button */}
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {/* Icon */}
        <div className={styles.iconWrapper}>
          <Icon />
        </div>

        {/* Title */}
        <h2 id="status-popup-title" className={styles.title}>
          {displayTitle}
        </h2>

        {/* Message */}
        {message && <p className={styles.message}>{message}</p>}

        {/* Action Button */}
        <button
          type="button"
          className={`${styles.actionButton} ${styles[buttonClasses[type]] || styles.buttonInfo}`}
          onClick={onClose}
        >
          {displayButtonText}
        </button>
      </div>
    </div>
  );
}
